/**
 * Hanlyn Collective — Custom JavaScript
 *
 * Custom components and interactions for the Hanlyn theme.
 * Extends Dawn's functionality without modifying core files.
 */

/**
 * HanlynNavAccordion
 *
 * Accordion component for the menu drawer navigation.
 * Handles expand/collapse of navigation submenus with proper ARIA attributes.
 *
 * Features:
 * - Only one submenu open at a time
 * - Smooth height-based animations (respects prefers-reduced-motion)
 * - Full keyboard accessibility
 * - Proper ARIA state management
 */
class HanlynNavAccordion extends HTMLElement {
  constructor() {
    super();

    this.triggers = this.querySelectorAll('[data-accordion-trigger]');

    // Check if reduced motion is preferred
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    this.triggers.forEach((trigger) => {
      // Use capture phase (true) to ensure our handler runs before Dawn's MenuDrawer handler
      trigger.addEventListener('click', this.handleTriggerClick.bind(this), true);
    });
  }

  disconnectedCallback() {
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleTriggerClick.bind(this), true);
    });
  }

  handleTriggerClick(event) {
    // Prevent Dawn's MenuDrawer handler from closing the drawer
    // Dawn attaches handlers to all buttons - we need to stop it immediately
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const trigger = event.currentTarget;
    const panelId = trigger.getAttribute('aria-controls');
    const panel = document.getElementById(panelId);

    if (!panel) return;

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      // Close this panel and any nested panels within it
      this.closePanel(trigger, panel);
      this.closePanelsWithin(panel);
    } else {
      // Close sibling panels at the same level (but not parent or child panels)
      this.closeSiblingPanels(trigger);

      // Open this panel
      this.openPanel(trigger, panel);
    }
  }

  closeSiblingPanels(trigger) {
    // Find the parent list that contains this trigger
    const parentList = trigger.closest('.hanlyn-drawer__nav-list, .hanlyn-drawer__nav-sublist');
    if (!parentList) return;

    // Find all triggers in the same parent list (siblings)
    const siblingTriggers = parentList.querySelectorAll(':scope > li [data-accordion-trigger]');

    siblingTriggers.forEach((siblingTrigger) => {
      if (siblingTrigger !== trigger && siblingTrigger.getAttribute('aria-expanded') === 'true') {
        const siblingPanelId = siblingTrigger.getAttribute('aria-controls');
        const siblingPanel = document.getElementById(siblingPanelId);
        if (siblingPanel) {
          this.closePanel(siblingTrigger, siblingPanel);
          // Also close any nested panels within the sibling
          this.closePanelsWithin(siblingPanel);
        }
      }
    });
  }

  closePanelsWithin(parentPanel) {
    // Close all nested accordion panels within this panel
    const nestedTriggers = parentPanel.querySelectorAll('[data-accordion-trigger][aria-expanded="true"]');
    nestedTriggers.forEach((nestedTrigger) => {
      const nestedPanelId = nestedTrigger.getAttribute('aria-controls');
      const nestedPanel = document.getElementById(nestedPanelId);
      if (nestedPanel) {
        this.closePanel(nestedTrigger, nestedPanel);
      }
    });
  }

  openPanel(trigger, panel) {
    // Update ARIA attributes
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');

    // Update icon
    const icon = trigger.querySelector('.hanlyn-drawer__nav-icon');
    if (icon) {
      icon.textContent = '−'; // Minus sign
    }

    // Animate height if motion is allowed
    if (!this.prefersReducedMotion) {
      // Get the natural height
      panel.style.maxHeight = panel.scrollHeight + 'px';
    } else {
      // Just show it immediately
      panel.style.maxHeight = 'none';
    }
  }

  closePanel(trigger, panel) {
    // Update ARIA attributes
    trigger.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');

    // Update icon
    const icon = trigger.querySelector('.hanlyn-drawer__nav-icon');
    if (icon) {
      icon.textContent = '+'; // Plus sign
    }

    // Animate height if motion is allowed
    if (!this.prefersReducedMotion) {
      panel.style.maxHeight = '0';
    } else {
      // Just hide it immediately
      panel.style.maxHeight = '0';
    }
  }
}

// Register the custom element
if (!customElements.get('hanlyn-nav-accordion')) {
  customElements.define('hanlyn-nav-accordion', HanlynNavAccordion);
}


/**
 * Extend HeaderDrawer to handle the new close button
 *
 * Dawn's HeaderDrawer class handles the main drawer open/close functionality.
 * We need to add a listener for our custom close button.
 */
document.addEventListener('DOMContentLoaded', () => {
  const closeButton = document.querySelector('.hanlyn-drawer__close');

  if (closeButton) {
    closeButton.addEventListener('click', () => {
      // Find the details element
      const details = document.getElementById('Details-menu-drawer-container');

      if (details && details.hasAttribute('open')) {
        // Find the summary to trigger Dawn's close logic
        const summary = details.querySelector('summary');
        if (summary) {
          summary.click();
        }
      }
    });
  }

  // Handle collection cards carousel navigation
  const cardsScroll = document.querySelector('[data-cards-scroll]');
  const prevButton = document.querySelector('[data-cards-prev]');
  const nextButton = document.querySelector('[data-cards-next]');

  if (cardsScroll && prevButton && nextButton) {
    const scrollAmount = 200; // Scroll by ~1 card width

    const updateButtons = () => {
      const isAtStart = cardsScroll.scrollLeft <= 0;
      const isAtEnd = cardsScroll.scrollLeft >= cardsScroll.scrollWidth - cardsScroll.clientWidth - 1;

      prevButton.disabled = isAtStart;
      nextButton.disabled = isAtEnd;
    };

    const handlePrevClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      cardsScroll.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    };

    const handleNextClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      cardsScroll.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    };

    // Use capture phase to run before Dawn's handler
    prevButton.addEventListener('click', handlePrevClick, true);
    nextButton.addEventListener('click', handleNextClick, true);

    cardsScroll.addEventListener('scroll', updateButtons);

    // Initial button state
    updateButtons();
  }
});


/**
 * HanlynProductCarousel
 *
 * Horizontally scrolling product carousel with arrow button controls and swipe support.
 * Manages scroll position, button states, overflow detection, and touch gestures.
 *
 * Features:
 * - Previous/next arrow buttons scroll by one card width + gap
 * - Buttons disable at start/end of scroll
 * - Hides arrows entirely if all content fits without scrolling
 * - Smooth scroll with prefers-reduced-motion support
 * - Touch swipe gestures on mobile for intuitive navigation
 */
class HanlynProductCarousel extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector('[data-track]');
    this.prevButton = this.querySelector('[data-arrow-prev]');
    this.nextButton = this.querySelector('[data-arrow-next]');
    this.arrowsContainer = this.querySelector('[data-arrows]');

    // Get card sizing from data attributes
    this.cardWidthDesktop = parseInt(this.dataset.cardWidthDesktop) || 288;
    this.cardWidthMobile = parseInt(this.dataset.cardWidthMobile) || 224;
    this.cardGap = parseInt(this.dataset.cardGap) || 16;

    // Check if reduced motion is preferred
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Debounce scroll handler
    this.scrollTimeout = null;

    // Touch/swipe tracking
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.isDragging = false;

    // Store bound handlers for cleanup
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  connectedCallback() {
    if (!this.track || !this.prevButton || !this.nextButton) return;

    // Add event listeners
    this.prevButton.addEventListener('click', this.handlePrevClick);
    this.nextButton.addEventListener('click', this.handleNextClick);
    this.track.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);

    // Add touch event listeners for swipe support on mobile
    // Note: Native CSS scrolling should work first. These handlers are for enhanced flick gestures.
    // this.track.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    // this.track.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    // this.track.addEventListener('touchend', this.handleTouchEnd, { passive: true });

    // Initial state check
    this.checkOverflow();
    this.updateButtonStates();
  }

  disconnectedCallback() {
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
    }
    if (this.track) {
      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('touchstart', this.handleTouchStart);
      this.track.removeEventListener('touchmove', this.handleTouchMove);
      this.track.removeEventListener('touchend', this.handleTouchEnd);
    }
    window.removeEventListener('resize', this.handleResize);

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  getScrollDistance() {
    // Determine card width based on viewport size
    const isMobile = window.innerWidth < 750;
    const cardWidth = isMobile ? this.cardWidthMobile : this.cardWidthDesktop;
    return cardWidth + this.cardGap;
  }

  handlePrevClick() {
    if (!this.track) return;

    const scrollDistance = this.getScrollDistance();
    const targetScroll = this.track.scrollLeft - scrollDistance;

    this.track.scrollTo({
      left: Math.max(0, targetScroll),
      behavior: this.prefersReducedMotion ? 'auto' : 'smooth'
    });
  }

  handleNextClick() {
    if (!this.track) return;

    const scrollDistance = this.getScrollDistance();
    const targetScroll = this.track.scrollLeft + scrollDistance;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;

    this.track.scrollTo({
      left: Math.min(maxScroll, targetScroll),
      behavior: this.prefersReducedMotion ? 'auto' : 'smooth'
    });
  }

  handleScroll() {
    // Debounce scroll event for performance
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.updateButtonStates();
    }, 50);
  }

  handleResize() {
    // Re-check overflow on window resize
    this.checkOverflow();
    this.updateButtonStates();
  }

  checkOverflow() {
    if (!this.track || !this.arrowsContainer) return;

    // Check if content overflows
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth;

    if (hasOverflow) {
      this.arrowsContainer.classList.remove('hidden');
    } else {
      this.arrowsContainer.classList.add('hidden');
    }
  }

  updateButtonStates() {
    if (!this.track || !this.prevButton || !this.nextButton) return;

    const scrollLeft = this.track.scrollLeft;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;

    // Previous button: disabled at start
    const isAtStart = scrollLeft <= 1; // 1px tolerance for sub-pixel rounding
    this.prevButton.setAttribute('aria-disabled', isAtStart);

    // Next button: disabled at end
    const isAtEnd = scrollLeft >= maxScroll - 1; // 1px tolerance for sub-pixel rounding
    this.nextButton.setAttribute('aria-disabled', isAtEnd);
  }

  /**
   * Touch/swipe gesture handlers for mobile navigation
   * Note: The carousel already has native touch scrolling via CSS overflow-x: scroll
   * with scroll-snap-type. These handlers are for quick flick gestures only.
   */
  handleTouchStart(event) {
    // Only enable swipe on mobile viewports
    if (window.innerWidth >= 750) return;

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.isDragging = false;

    // Store initial scroll position
    this.scrollStartLeft = this.track.scrollLeft;
  }

  handleTouchMove(event) {
    // Only enable swipe on mobile viewports
    if (window.innerWidth >= 750) return;

    if (!this.touchStartX || !this.touchStartY) return;

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - this.touchStartX);
    const deltaY = Math.abs(touch.clientY - this.touchStartY);

    // Track if user is dragging horizontally
    if (deltaX > deltaY && deltaX > 5) {
      this.isDragging = true;
    }
  }

  handleTouchEnd(event) {
    // Only enable swipe on mobile viewports
    if (window.innerWidth >= 750) return;

    if (!this.touchStartX || !this.touchStartTime) {
      this.resetTouchTracking();
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    const deltaTime = Date.now() - this.touchStartTime;
    const scrollDelta = this.track.scrollLeft - this.scrollStartLeft;

    // Calculate swipe distance and velocity
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const velocity = absDeltaX / deltaTime; // pixels per ms

    // Only trigger programmatic scroll for quick flick gestures
    // If user already dragged significantly, let the native snap behavior handle it
    const wasQuickFlick = deltaTime < 300 && velocity > 0.5;
    const hadMinimalNativeScroll = Math.abs(scrollDelta) < 30;
    const isHorizontalGesture = absDeltaX > absDeltaY;

    if (wasQuickFlick && hadMinimalNativeScroll && isHorizontalGesture && absDeltaX > 30) {
      // Quick flick detected - trigger card-by-card navigation
      if (deltaX < 0) {
        // Flicked left - go to next
        this.handleNextClick();
      } else {
        // Flicked right - go to previous
        this.handlePrevClick();
      }
    }
    // Otherwise, let the native scroll + snap behavior handle the result

    this.resetTouchTracking();
  }

  resetTouchTracking() {
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.scrollStartLeft = 0;
    this.isDragging = false;
  }
}

// Register the custom element
if (!customElements.get('hanlyn-product-carousel')) {
  customElements.define('hanlyn-product-carousel', HanlynProductCarousel);
}
